import * as CL from '@design-system/component-library';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import type { RefObject } from 'react';

import './ScrollHintButton.scss';

const VISIBLE_RATIO_THRESHOLD = 0.3;

export interface ScrollHintButtonProps {
  targetRef: RefObject<HTMLElement | null>;
  // omit to observe against the viewport
  containerRef?: RefObject<HTMLElement | null>;
  ariaLabel: string;
  icon?: string;
  className?: string;
}

export const ScrollHintButton = ({
  targetRef,
  containerRef,
  ariaLabel,
  icon = 'arrow-down',
  className,
}: ScrollHintButtonProps) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!targetRef.current) {
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Not enough visible yet, or already scrolled past it
        const notEnoughVisible = entry.intersectionRatio < VISIBLE_RATIO_THRESHOLD;
        const notScrolledPast = !entry.rootBounds || entry.boundingClientRect.top > entry.rootBounds.top;
        setVisible(notEnoughVisible && notScrolledPast);
      },
      { root: containerRef?.current ?? null, threshold: [0, VISIBLE_RATIO_THRESHOLD] }
    );
    observer.observe(targetRef.current);
    return () => observer.disconnect();
  }, [targetRef, containerRef]);

  if (!visible) {
    return null;
  }

  const handleClick = () => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    targetRef.current?.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'start' });
  };

  return (
    // Plain button, not CL.Button: avoids overriding DS button's border/border-radius/padding for circular shape
    <button
      type="button"
      className={classNames('of-scroll-hint-button', className)}
      aria-label={ariaLabel}
      onClick={handleClick}
    >
      <CL.Icon icon={icon} type="filled" size="m" color="neutral-700" />
    </button>
  );
};
